import getVaultKeyListUrl from "./urls/getVaultKeyListUrl";

import HttpServiceSingleton from "../../misc/HttpService/HttpService";
import getSecretUrl from "./urls/getSecretUrl";
import getVersionHistoryUrl from "./urls/getVersionHistoryUrl";

/**
 * Fetches vault key list from API with pagination metadata
 * @param {string} searchTerm - Search term to filter keys
 * @param {number} limit - Maximum number of keys to return
 * @param {number} offset - Byte offset for server-side pagination
 * @returns {Promise<{data: Array, totalCount: number, nextOffset: number}>}
 */
export const getVaultKeyList = (searchTerm, limit = 100, offset = 0) => {
  const url = getVaultKeyListUrl(searchTerm, limit, offset);
  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${HttpServiceSingleton.cookies.get("token_type")} ${HttpServiceSingleton.cookies.get("access_token")}`,
    },
  }).then((res) => {
    if (res.status === 401) {
      localStorage.removeItem("site");
      sessionStorage.removeItem("startVerified");
      // path:/ is required — the cookie is set at path=/, and remove() without a
      // matching path does NOT delete it (leaving a stale token behind).
      HttpServiceSingleton.cookies.remove("token_type", { path: "/" });
      HttpServiceSingleton.cookies.remove("access_token", { path: "/" });
      window.location.reload();
      return { data: [], totalCount: 0, nextOffset: 0 };
    }
    if (res.status === 200) {
      const totalCount = parseInt(res.headers.get("X-Total-Count") || "0", 10);
      const nextOffset = parseInt(res.headers.get("X-Next-Offset") || "0", 10);
      return res.json().then((data) => ({ data, totalCount, nextOffset }));
    }
    return { data: [], totalCount: 0, nextOffset: 0 };
  });
};

/**
 * Sends a keepalive ping to reset server idle/session timer.
 * If the server returns a refreshed token, stores it.
 * @param {string} [reason] - "continue" when triggered by the explicit
 *   "Continue Session" button, so the backend audits it as a session extension
 *   (distinct from a background activity keepalive).
 */
export const sendKeepalive = (reason) =>
  fetch(`/api/v1/bg/keepalive${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${HttpServiceSingleton.cookies.get("token_type")} ${HttpServiceSingleton.cookies.get("access_token")}`,
    },
  }).then((res) => {
    if (res.status === 200) {
      return res.json().then((data) => {
        if (data.token) {
          localStorage.setItem("site", data.token);
          // path:/ so keepalive updates the SAME cookie login set (not a dup at
          // the current path), keeping getSecret's cookie read consistent.
          HttpServiceSingleton.cookies.set("access_token", data.token, { path: "/" });
        }
        return data;
      });
    }
    return res;
  }).catch(() => {});

/**
 * Fetches secret value for a specific key
 * @param {string} keyId - The key identifier
 * @param {number} version - Optional version number
 * @returns {Promise} API response with secret data
 */
export const getSecret = (keyId, version) => 
  HttpServiceSingleton.get(getSecretUrl(keyId, version));

/**
 * Fetches version history for a specific key
 * @param {string} keyId - The key identifier
 * @returns {Promise} API response with version history data
 */
export const getVersionHistory = (keyId) => 
  HttpServiceSingleton.get(getVersionHistoryUrl(keyId));
